import React, { useCallback, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { pie } from 'd3-shape';
import { KeyCode } from '../internal/keycode';
import { nodeContains } from '../internal/utils/dom';
import { fireNonCancelableEvent } from '../internal/events';
import { useUniqueId } from '../internal/hooks/use-unique-id';
import ChartPopover from '../internal/components/chart-popover';
import SeriesDetails from '../internal/components/chart-series-details';
import SeriesMarker from '../internal/components/chart-series-marker';
import ChartStatusContainer, { getChartStatus } from '../internal/components/chart-status-container';
import InternalBox from '../box/internal';
import Labels from './labels';
import styles from './styles.css.js';
import { defaultDetails, dimensionsBySize } from './utils';
import Segments from './segments';
export default (function (_a) {
    var _b;
    var _c = _a.variant, variant = _c === void 0 ? 'pie' : _c, _d = _a.size, size = _d === void 0 ? 'medium' : _d, _e = _a.i18nStrings, i18nStrings = _e === void 0 ? {} : _e, ariaLabel = _a.ariaLabel, ariaLabelledby = _a.ariaLabelledby, data = _a.data, visibleData = _a.visibleData, ariaDescription = _a.ariaDescription, innerMetricValue = _a.innerMetricValue, innerMetricDescription = _a.innerMetricDescription, _f = _a.hideTitles, hideTitles = _f === void 0 ? false : _f, _g = _a.hideDescriptions, hideDescriptions = _g === void 0 ? false : _g, detailPopoverContent = _a.detailPopoverContent, detailPopoverSize = _a.detailPopoverSize, width = _a.width, additionalFilters = _a.additionalFilters, hideFilter = _a.hideFilter, hideLegend = _a.hideLegend, _h = _a.statusType, statusType = _h === void 0 ? 'finished' : _h, empty = _a.empty, noMatch = _a.noMatch, errorText = _a.errorText, recoveryText = _a.recoveryText, loadingText = _a.loadingText, onRecoveryClick = _a.onRecoveryClick, segmentDescription = _a.segmentDescription, onHighlightChange = _a.onHighlightChange, highlightedSegment = _a.highlightedSegment, setHighlightedSegment = _a.setHighlightedSegment, pinnedSegment = _a.pinnedSegment, setPinnedSegment = _a.setPinnedSegment;
    var dimensions = dimensionsBySize[size];
    var radius = dimensions.outerRadius;
    var hasLabels = !(hideTitles && hideDescriptions);
    var height = 2 * (radius + dimensions.padding + (hasLabels ? dimensions.paddingLabels : 0));
    // Inner content is only available for donut charts and the inner description is not displayed for small charts
    var hasInnerContent = variant === 'donut' && (innerMetricValue || (innerMetricDescription && size !== 'small'));
    var descriptionId = useUniqueId('awsui-pie-chart__description');
    var innerMetricId = useUniqueId('awsui-pie-chart__inner');
    var ariaDescribedBy = [ariaDescription ? descriptionId : null, hasInnerContent ? innerMetricId : null]
        .filter(function (id) { return !!id; })
        .join(' ');
    var _j = useState(false), isTooltipOpen = _j[0], setTooltipOpen = _j[1];
    var _k = useState(), tooltipData = _k[0], setTooltipData = _k[1];
    var _l = useMemo(function () {
        var dataSum = visibleData.reduce(function (sum, d) { return sum + d.datum.value; }, 0);
        var pieFactory = pie()
            // Minimum 1% segment size
            .value(function (d) { return (d.datum.value < dataSum / 100 ? dataSum / 100 : d.datum.value); })
            .sort(null);
        // Filter out segments with value of zero or below
        var pieData = pieFactory(visibleData.filter(function (d) { return d.datum.value > 0; }));
        return { pieData: pieData, dataSum: dataSum };
    }, [visibleData]), pieData = _l.pieData, dataSum = _l.dataSum;
    var detailFunction = detailPopoverContent || defaultDetails(i18nStrings);
    var tooltipContent = tooltipData && React.createElement(SeriesDetails, { details: detailFunction(tooltipData.datum, dataSum) });
    var containerRef = useRef(null);
    var segmentsRef = useRef(null);
    var focusOutlineRef = useRef(null);
    var _m = getChartStatus({ externalData: data, visibleData: pieData, statusType: statusType }), isEmpty = _m.isEmpty, showChart = _m.showChart;
    // Pie charts have a special condition for empty/noMatch due to how zero-value segments are handled.
    var isNoMatch = isEmpty && visibleData.length !== data.length;
    var reserveLegendSpace = !showChart && !hideLegend;
    var reserveFilterSpace = statusType !== 'finished' && !isNoMatch && (!hideFilter || additionalFilters);
    var popoverDismissedRecently = useRef(false);
    var highlightSegment = useCallback(function (internalDatum, trigger) {
        var segment = internalDatum.datum;
        if (segment !== highlightedSegment) {
            setHighlightedSegment(segment);
            fireNonCancelableEvent(onHighlightChange, {
                highlightedSegment: segment
            });
        }
        if (trigger) {
            setTooltipData({
                datum: internalDatum.datum,
                series: {
                    color: internalDatum.color,
                    index: internalDatum.index,
                    label: internalDatum.datum.title,
                    markerType: 'rectangle'
                },
                trigger: trigger
            });
            setTooltipOpen(true);
        }
    }, [highlightedSegment, setTooltipOpen, setHighlightedSegment, onHighlightChange]);
    var clearHighlightedSegment = useCallback(function () {
        setTooltipOpen(false);
        setHighlightedSegment(null);
        fireNonCancelableEvent(onHighlightChange, {
            highlightedSegment: null
        });
    }, [setHighlightedSegment, onHighlightChange, setTooltipOpen]);
    var onClick = useCallback(function (internalDatum, triggerRef) {
        if (pinnedSegment === internalDatum.datum) {
            setPinnedSegment(null);
            clearHighlightedSegment();
        }
        else {
            setPinnedSegment(internalDatum.datum);
            highlightSegment(internalDatum, triggerRef);
        }
    }, [pinnedSegment, clearHighlightedSegment, setPinnedSegment, highlightSegment]);
    var onMouseOver = useCallback(function (internalDatum, triggerRef) {
        if (pinnedSegment !== null) {
            return;
        }
        highlightSegment(internalDatum, triggerRef);
    }, [pinnedSegment, highlightSegment]);
    var onMouseOut = useCallback(function () {
        if (pinnedSegment !== null) {
            return;
        }
        clearHighlightedSegment();
    }, [pinnedSegment, clearHighlightedSegment]);
    var onKeyDown = useCallback(function (index, triggerRef, event) {
        if (event.keyCode !== KeyCode.right && event.keyCode !== KeyCode.left && event.keyCode !== KeyCode.enter) {
            return;
        }
        event.preventDefault();
        var nextIndex = index;
        var MAX = pieData.length - 1;
        if (event.keyCode === KeyCode.right) {
            nextIndex++;
            if (nextIndex > MAX) {
                nextIndex = 0;
            }
        }
        else if (event.keyCode === KeyCode.left) {
            nextIndex--;
            if (nextIndex < 0) {
                nextIndex = MAX;
            }
        }
        if (event.keyCode === KeyCode.enter) {
            setPinnedSegment(pieData[nextIndex].data.datum);
        }
        highlightSegment(pieData[nextIndex].data, triggerRef);
    }, [setPinnedSegment, highlightSegment, pieData]);
    var onFocus = useCallback(function (internalDatum, triggerRef) {
        // We need to make sure that we do not re-show the popover when we focus the segment after the popover is dismissed.
        // Normally we would check `event.relatedTarget` for the previously focused element,
        // but this is not supported for SVG elements in IE11. The workaround is this `popoverDismissedRecently` ref.
        if (pinnedSegment !== null || popoverDismissedRecently.current) {
            return;
        }
        highlightSegment(internalDatum, triggerRef);
    }, [pinnedSegment, highlightSegment]);
    var onBlur = useCallback(function (event) {
        var blurTarget = event.relatedTarget || event.target;
        if (blurTarget === null || !(blurTarget instanceof Element) || !nodeContains(containerRef.current, blurTarget)) {
            clearHighlightedSegment();
            setPinnedSegment(null);
        }
    }, [clearHighlightedSegment, setPinnedSegment]);
    var onPopoverDismiss = function () {
        setTooltipOpen(false);
        setPinnedSegment(null);
        // Return focus back to the segment if possible
        var target = tooltipData === null || tooltipData === void 0 ? void 0 : tooltipData.trigger.parentNode;
        if (target) {
            // We disable `returnFocus` on the popover because the library won't be able to focus an SVG element in IE11.
            // Therefore we need to return focus ourselves.
            // We are using `setTimeout` as suggested by react-focus-lock when using `returnFocus={false}`.
            // See https://github.com/theKashey/react-focus-lock#unmounting-and-focus-management
            setTimeout(function () {
                popoverDismissedRecently.current = true;
                if (target.focus) {
                    target.focus();
                }
                else {
                    window.HTMLElement.prototype.focus.call(target);
                }
                popoverDismissedRecently.current = false;
            }, 0);
        }
    };
    return (React.createElement("div", { className: clsx(styles.content, styles["content--" + size], (_b = {},
            _b[styles['content--reserve-filter']] = reserveFilterSpace,
            _b[styles['content--reserve-legend']] = reserveLegendSpace,
            _b)) },
        React.createElement(ChartStatusContainer, { isEmpty: isEmpty, isNoMatch: isNoMatch, showChart: showChart, statusType: statusType, empty: empty, noMatch: noMatch, loadingText: loadingText, errorText: errorText, recoveryText: recoveryText, onRecoveryClick: onRecoveryClick }),
        showChart && (React.createElement("div", { className: styles['chart-container'], ref: containerRef, onBlur: onBlur },
            React.createElement("svg", { width: width, height: height, className: styles.pie, role: "graphics-document", "aria-hidden": "false", "aria-label": ariaLabel, "aria-labelledby": ariaLabelledby, "aria-describedby": ariaDescribedBy || undefined, "aria-roledescription": i18nStrings === null || i18nStrings === void 0 ? void 0 : i18nStrings.chartAriaRoleDescription, focusable: "false" },
                ariaDescription && React.createElement("title", { id: descriptionId }, ariaDescription),
                React.createElement("g", { transform: "translate(" + width / 2 + " " + height / 2 + ")", role: "group", ref: segmentsRef },
                    React.createElement(Segments, { pieData: pieData, size: size, variant: variant, containerRef: containerRef, focusOutlineRef: focusOutlineRef, highlightedSegment: highlightedSegment, segmentAriaRoleDescription: i18nStrings === null || i18nStrings === void 0 ? void 0 : i18nStrings.segmentAriaRoleDescription, onClick: onClick, onMouseOver: onMouseOver, onMouseOut: onMouseOut, onKeyDown: onKeyDown, onFocus: onFocus }),
                    hasLabels && (React.createElement(Labels, { pieData: pieData, size: size, segmentDescription: segmentDescription, visibleDataSum: dataSum, hideTitles: hideTitles, hideDescriptions: hideDescriptions, highlightedSegment: highlightedSegment })))),
            hasInnerContent && (React.createElement("div", { className: styles['inner-content'], id: innerMetricId },
                innerMetricValue && (React.createElement(InternalBox, { fontSize: size === 'small' ? 'heading-m' : 'heading-xl' }, innerMetricValue)),
                innerMetricDescription && size !== 'small' && (React.createElement(InternalBox, { color: "text-body-secondary", fontSize: "heading-m" }, innerMetricDescription)))),
            isTooltipOpen && tooltipData && (React.createElement(ChartPopover, { title: tooltipData.series && (React.createElement(InternalBox, { className: styles['popover-header'], variant: "strong" },
                    React.createElement(SeriesMarker, { color: tooltipData.series.color, type: tooltipData.series.markerType }),
                    ' ',
                    tooltipData.series.label)), trigger: tooltipData.trigger, dismissButton: pinnedSegment !== null, dismissAriaLabel: i18nStrings.detailPopoverDismissAriaLabel, onDismiss: onPopoverDismiss, container: segmentsRef.current, size: detailPopoverSize }, tooltipContent))))));
});
//# sourceMappingURL=pie-chart.js.map